import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { TablerIcon } from "@/components/icons/TablerIcon";
import { useAuth } from "@/contexts/AuthContext";
import { listMvpDraftSummaries, type MvpDraftSummary } from "@/hooks/use-mvp-persistence";

function formatDraftDate(ts: number | null) {
  if (ts == null) return "—";
  return new Date(ts).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function DashboardOfertyPage() {
  const { user } = useAuth();
  const [drafts, setDrafts] = useState<MvpDraftSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const sortedDrafts = useMemo(
    () => [...drafts].sort((a, b) => b.updatedAt - a.updatedAt),
    [drafts],
  );

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const allDrafts = await listMvpDraftSummaries(user.id);
      if (cancelled) return;
      setDrafts(allDrafts);
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <DashboardLayout>
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">Oferty</h1>
        </div>
        <Link to="/mvp/new" className="dashboard-btn-primary">
          <TablerIcon name="plus" size={18} />
          Nowa oferta
        </Link>
      </header>

      <section className="dashboard-section">
        {loading ? (
          <div className="dashboard-empty dashboard-empty--compact">Ładowanie…</div>
        ) : sortedDrafts.length === 0 ? (
          <div className="dashboard-empty-card">
            <p className="dashboard-empty-title">Brak szkiców.</p>
            <p className="dashboard-empty-desc">Stwórz pierwszą ofertę.</p>
            <Link to="/mvp/new" className="dashboard-btn-primary">
              <TablerIcon name="plus" size={18} />
              Nowa oferta
            </Link>
          </div>
        ) : (
          <div className="dashboard-draft-list">
            {sortedDrafts.map((draft) => (
              <div key={draft.id} className="dashboard-draft-row">
                <Link to={`/mvp/${draft.id}`} className="dashboard-draft-link">
                  <TablerIcon name="file-text" size={20} className="text-[var(--ochre)]" />
                  <div className="min-w-0 flex-1">
                    <p className="dashboard-draft-name">{draft.name}</p>
                    <p className="dashboard-card-meta">
                      <TablerIcon name="clock" size={14} />
                      {formatDraftDate(draft.updatedAt)}
                    </p>
                  </div>
                  <span className="dashboard-draft-cta">Otwórz →</span>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
